import { useEffect, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { format } from 'date-fns';
import { useSelectedView } from 'reducers/viewsSlice';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { useOneOnOneInsightsFiltersData } from 'screens/oneOnOneRabbit/Insights/slice/OneOnOneInsightsFiltersSlice';
import { ONE_ON_ONE_COMPLETION_STATUS } from 'screens/oneOnOneRabbit/InstancePage/features/RIghtSidebar/features/OneOnOne/OneOnOneSection';
import { ONE_ON_ONE_RELATION_MAP } from 'screens/oneOnOneRabbit/creationDrawer/ParticipantSelectionComp';
import { getColumnDefinitionByType } from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { AgGridSearchBar } from 'uiToolkitV1/AgGridSearchBar';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { PaperWithShadow } from 'uiToolkitV1/Paper';

import { useGenerateOneOnOneDataExportsReport } from '../hooks/dataHooks';

function OneOnOneDataExportsReportGrid({ gridRef, gridStatePersistenceKey }) {
  const cohortDetails = useSelectedView();
  const { dateRange } = useOneOnOneInsightsFiltersData();

  const [columnDefs, setColumnDefs] = useState([]);

  const { isLoading, data: oneOnOneDataExportsReportsData } = useGenerateOneOnOneDataExportsReport({
    cohortId: cohortDetails?.id,
    startTS: dateRange?.startDate.getTime(),
    endTS: dateRange?.endDate.getTime(),
    enabled: !!cohortDetails?.id && !!dateRange?.startDate && !!dateRange?.endDate,
  });

  const { columns, rows = [] } = oneOnOneDataExportsReportsData ?? {};

  useEffect(() => {
    if (!columns || columns.length === 0) return;

    // Define which columns should be visible by default
    const defaultVisibleColumns = [
      'EMPLOYEE_NAME',
      'OOO_DATE',
      'MENTOR_NAME',
      'EMPLOYEE_MENTOR_RELATION',
      'MEETING_SCHEDULED_BY',
      'STATUS',
    ];

    const newColumnDefs = columns.map((col) => {
      // Determine if this column should be hidden by default
      const isHidden = !defaultVisibleColumns.includes(col.columnId);

      // Special handling for EMPLOYEE_MENTOR_RELATION column
      if (col.columnId === 'EMPLOYEE_MENTOR_RELATION') {
        return {
          colId: col.columnId,
          field: col.columnId,
          id: col.columnId,
          headerName: col.displayName,
          minWidth: 220,
          filter: 'agSetColumnFilter',
          headerComponent: CustomHeaderCell,
          headerComponentParams: {
            enableMenu: true,
            enableSorting: true,
            displayName: col.displayName,
          },
          menuTabs: ['filterMenuTab', 'generalMenuTab'],
          autoHeaderHeight: true,
          wrapHeaderText: true,
          columnType: col.columnType,
          metadata: col.metadata,
          parentColumnId: col.parentColumnId,
          hide: isHidden,
          valueGetter: (params) => {
            const empRelation = params.data?.EMPLOYEE_MENTOR_RELATION;
            return ONE_ON_ONE_RELATION_MAP[empRelation] ?? '';
          },
        };
      }

      // Special handling for STATUS column
      if (col.columnId === 'STATUS') {
        return {
          colId: col.columnId,
          field: col.columnId,
          id: col.columnId,
          headerName: col.displayName,
          minWidth: 220,
          filter: 'agSetColumnFilter',
          headerComponent: CustomHeaderCell,
          headerComponentParams: {
            enableMenu: true,
            enableSorting: true,
            displayName: col.displayName,
          },
          menuTabs: ['filterMenuTab', 'generalMenuTab'],
          autoHeaderHeight: true,
          wrapHeaderText: true,
          columnType: col.columnType,
          metadata: col.metadata,
          parentColumnId: col.parentColumnId,
          hide: isHidden,
          valueGetter: (params) => {
            const status = params.data?.STATUS;
            return ONE_ON_ONE_COMPLETION_STATUS[status]?.label ?? '';
          },
        };
      }

      // Special handling for OOO_DATE column
      if (col.columnId === 'OOO_DATE') {
        return {
          colId: col.columnId,
          field: col.columnId,
          id: col.columnId,
          headerName: col.displayName,
          minWidth: 220,
          headerComponent: CustomHeaderCell,
          headerComponentParams: {
            enableMenu: true,
            enableSorting: true,
            displayName: col.displayName,
          },
          menuTabs: ['filterMenuTab', 'generalMenuTab'],
          autoHeaderHeight: true,
          wrapHeaderText: true,
          columnType: col.columnType,
          metadata: col.metadata,
          parentColumnId: col.parentColumnId,
          hide: isHidden,
          valueFormatter: (params) => {
            const dateValue = params.data?.OOO_DATE;
            if (!dateValue) return '';

            const date = new Date(dateValue * 1000); // Convert from seconds to milliseconds

            return format(date, 'MMM dd, yyyy, hh:mm a');
          },
          valueGetter: (params) => {
            const dateValue = params.data?.OOO_DATE;
            if (!dateValue) return '';

            const date = new Date(dateValue * 1000); // Convert from seconds to milliseconds

            // Format: M/D/YYYY H:MM:SS TZ (e.g., "7/10/2025 0:53:24 CST")
            return format(date, 'M/d/yyyy H:mm:ss zzz');
          },
        };
      }

      const baseColDef = {
        colId: col.columnId,
        field: col.columnId,
        id: col.columnId,
        headerName: col.displayName,
        minWidth: 220,
        headerComponent: CustomHeaderCell,
        headerComponentParams: {
          enableMenu: true,
          enableSorting: true,
          displayName: col.displayName,
        },
        hide: isHidden,
        lockVisible: col.columnId === 'EMPLOYEE_NAME', // Lock EMPLOYEE_NAME column so it can't be hidden
        flex: col.columnId === 'EMPLOYEE_NAME' ? 1 : undefined, // Flex for EMPLOYEE_NAME to take available space
        ...getColumnDefinitionByType(col),
      };

      return baseColDef;
    });

    setColumnDefs(newColumnDefs);
  }, [columns]);

  return (
    <PaperWithShadow p={4} mb={1} mt={checkNewNavEnabled() ? 0 : 3} position="relative">
      <Flex alignItems="center" justifyContent="space-between" pb={2}>
        <Box w="300px">
          <AgGridSearchBar gridRef={gridRef} />
        </Box>
      </Flex>
      <Box className="one-on-one-data-exports-report-grid">
        <AgGridTable
          key={gridStatePersistenceKey} // we want to re-initialize on key change
          gridStatePersistenceKey={gridStatePersistenceKey}
          ref={gridRef}
          rowData={rows}
          columnDefs={columnDefs}
          domLayout="autoHeight"
          pagination
          paginationPageSize={10}
          headerHeight={36}
          rowHeight={56}
          kebabMenuIsRowSpecific={false}
          containerAutoHeight
          rowStyle={{ cursor: 'default' }}
          defaultColDef={{ cellStyle: { display: 'flex', alignItems: 'center' } }}
          loading={isLoading}
        />
      </Box>
    </PaperWithShadow>
  );
}

export { OneOnOneDataExportsReportGrid };
